import { isPlatformBrowser } from '@angular/common';
import type { AfterContentInit, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { Component, ElementRef, Inject, Input, NgZone, PLATFORM_ID, ViewChild } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { Image } from '@core-mkt/interfaces/image';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import type { Slide } from './slider';
import { SliderSection } from './slider';

@Component({
  selector: 'slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  standalone: true,
  imports: [LazyImgDirective, QueryParamDirective],
})
export class SliderComponent implements OnInit, AfterContentInit, AfterViewInit, OnDestroy {
  @Input() data: SliderSection;
  theme: ThemeInterface;
  containerClasses = '';
  carouselClasses = '';
  containsEyebrow: boolean;
  sliderId = 'slider-' + Math.random().toString(16).slice(2);
  @ViewChild('swiperEl') swiperEl?: ElementRef<HTMLElement>;
  @ViewChild('paginationEl') paginationEl?: ElementRef<HTMLElement>;
  swiper?: Swiper;
  public swiperConfig: SwiperOptions = {
    modules: [Pagination],
    // The removed swiper/angular wrapper always set observer: true; Swiper 12 defaults it to
    // false. Restored so slides re-rendered after init are still picked up, matching v8.
    observer: true,
    pagination: {
      type: 'bullets',
      clickable: true,
    },
  };

  constructor(
    private redactor: WysiwygRedactorService,
    private themeParser: ThemeParserService,
    private imgixService: ImgixService,
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone,
  ) {}

  get sliderTitle(): string {
    return this.data.sliderTitle;
  }

  get asideContent(): SafeHtml {
    return this.data.asideContent;
  }

  get sliderSlides(): Slide[] {
    return this.data.sliderSlides;
  }

  get image(): Image {
    if (this.data.sideContentStateSeal && !!this.data.sideContentStateSeal[0]) {
      return this.data.sideContentStateSeal[0];
    }
    return null;
  }

  ngOnInit(): void {
    // Check to see if aside content contains an eyebrow
    if (this.data.asideContent && typeof this.data.asideContent == 'string') {
      this.containsEyebrow = this.data.asideContent.includes('class="eyebrow"');
    }
    this.data.asideContent = this.redactor.bypassSanitizer(this.data.asideContent);
    this.data.sliderTitle = this.redactor.bypassSanitizer(this.data.sliderTitle);
    this.data.sliderSlides.forEach((slide) => {
      slide.slideContent = this.redactor.bypassSanitizer(slide.slideContent);
    });
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: null,
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }

  ngAfterContentInit(): void {
    if (this.data.asideContent) {
      this.containerClasses = 'lg:flex';
      this.carouselClasses = 'lg:w-1/2';
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swiperEl) return;
    // Bind pagination to the element itself rather than a selector string so the
    // lookup stays scoped to this instance instead of searching the whole document.
    // Swiper is constructed outside Angular's zone. The removed swiper/angular wrapper did
    // the same (swiper_angular.mjs), and it matters here: Swiper 12 registers pointermove /
    // touchmove listeners on `document`, and this app runs provideZoneChangeDetection() with
    // no event coalescing -- in-zone, every mouse move anywhere on the page would run a full
    // change-detection tick per carousel. Nothing in this component needs Angular to re-render
    // in response to a Swiper event.
    this.ngZone.runOutsideAngular(() => {
      this.swiper = new Swiper(this.swiperEl.nativeElement, {
        ...this.swiperConfig,
        pagination: { ...(this.swiperConfig.pagination as object), el: this.paginationEl?.nativeElement },
      });
    });
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
  }

  getImgStyle(img): string {
    return `height:${img.height}px;width:auto`;
  }

  private showAsideContent(sliderPlacement: string): boolean {
    return this.data.sliderAlignment === sliderPlacement;
  }
}
